import React, { useState, useMemo } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import { useUserPermissions, useRoles } from '../hooks/useAuthorizationData';
import { unassignRole } from '../services/authorizationService';
import RoleAssignDrawer from './RoleAssignDrawer';
import RolePermissionModal from './RolePermissionModal';
import DataTable from '../../../globalComponents/ui/DataTable/DataTable';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 12px 0;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const UserPermissionsPage = ({ user, onBack }) => {
  const { data: permissions, isLoading, refetch } = useUserPermissions(user?.id);
  const { data: allRoles } = useRoles();
  
  const [drawerRole, setDrawerRole] = useState(null);
  const [viewRole, setViewRole] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const roleAssignments = useMemo(() => {
    const assignments = (permissions || []).filter(p => p.roleId);
    // Deduplicate by roleId
    const unique = [];
    const seen = new Set();
    for (const a of assignments) {
      if (!seen.has(a.roleId)) {
        seen.add(a.roleId);
        unique.push(a);
      }
    }
    return unique;
  }, [permissions]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleAssignRole = () => {
    setDrawerRole(true);
  };

  const handleUnassignRole = async (row) => {
    const roleObj = allRoles?.find(r => String(r.id) === String(row.roleId));
    const roleName = roleObj?.name || row.roleName || 'Role';
    if (window.confirm(`Are you sure you want to remove the role "${roleName}" from this user?`)) {
      try {
        await unassignRole({
          userId: user?.id,
          roleId: row.roleId
        });
        refetch();
        showSuccess(`Role "${roleName}" removed successfully`);
      } catch (e) {
        alert(e?.message || 'Failed to remove role');
      }
    }
  };

  // Define columns for the DataTable
  const columns = [
    {
      label: 'Role Name',
      key: 'roleName',
      sortable: true,
      renderCell: (value, row) => {
        const roleObj = allRoles?.find(r => String(r.id) === String(row.roleId));
        return roleObj?.name || row.roleName || 'Unknown Role';
      },
    }
  ];

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading user roles...</p>
      </div>
    );
  }

  return (
    <PageContainer>
      <HeaderSection>
        <button className="btn btn-light" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
          Back
        </button>
        <div>
          <h4 className="mb-0">{user.name || user.email}</h4>
          <span className="text-muted">{user.email}</span>
        </div>
      </HeaderSection>

      {successMsg && <Alert variant="success">{successMsg}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Assigned Roles</h5>
        <Button variant="primary" onClick={handleAssignRole}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Assign Role
        </Button>
      </div>

      <div className="card shadow-sm border-0">
        <DataTable
          columns={columns}
          data={roleAssignments}
          onView={(row) => {
            const roleObj = allRoles.find(r => String(r.id) === String(row.roleId)) || { id: row.roleId, name: row.roleName };
            setViewRole(roleObj);
          }}
          onDelete={handleUnassignRole}
          pagination
          highlightOnHover
          noDataComponent={<div className="p-4 text-muted">No roles assigned to this user.</div>}
        />
      </div>

      <RoleAssignDrawer
        show={!!drawerRole}
        onHide={() => setDrawerRole(null)}
        role={null}
        userId={user?.id}
        onAssigned={() => {
          refetch();
          showSuccess(`Role assigned successfully`);
          setDrawerRole(null);
        }}
      />

      <RolePermissionModal
        show={!!viewRole}
        onHide={() => setViewRole(null)}
        role={viewRole}
      />

    </PageContainer>
  );
};

export default UserPermissionsPage;